import { Link } from '@tanstack/react-router';

import { type CoachTqReviewStudent } from '@airreading/common/data';

import { ArrowTooltip } from '@/components/arrow-tooltip.js';
import { CopyButton } from '@/components/copy-button.js';
import { LinkToExternal } from '@/components/link-to-external.js';
import { StudentNeedsIndicators } from '@/components/student-needs-indicators.js';
import { Badge } from '@/components/ui/badge.js';
import { cn } from '@/lib/tailwind-utils.js';

const classNotesActionClassName = 'h-7 px-2 text-xs font-normal';

/** `compact` is the dense variant used in the PM/GLA sessions popup: truncated text and `-` fallbacks. */
type CompactProps = { compact?: boolean };

export function TqReviewStudentsCell({ students, compact }: { students: CoachTqReviewStudent[] } & CompactProps) {
  if (students.length === 0) return compact ? '-' : '—';
  return (
    <div className={cn('flex flex-wrap gap-1', compact ? 'min-w-0' : 'min-w-40')}>
      {students.map((student) => (
        <Badge
          key={student.id}
          variant="outline"
          className={cn('text-left rounded-sm px-1 w-fit', compact && 'max-w-28 font-normal')}
          title={compact ? student.fullName : undefined}
        >
          <span className="inline-flex items-center gap-1 min-w-0">
            <StudentNeedsIndicators
              specialEd={student.specialEd}
              dyslexic={student.dyslexic}
              ell={student.ell}
              mode="icons"
            />
            <span className={cn(compact && 'truncate')}>{student.fullName}</span>
          </span>
        </Badge>
      ))}
    </div>
  );
}

export function TqReviewStudentIssuesCell({ studentIssues, compact }: { studentIssues: string | null } & CompactProps) {
  if (!studentIssues) return '-';
  return (
    <ArrowTooltip
      position="bottom"
      trigger={
        <div className={cn('text-left', compact ? 'max-w-40 truncate' : 'max-w-72 whitespace-pre-wrap line-clamp-2')}>
          {studentIssues}
        </div>
      }
    >
      <div className="max-w-md whitespace-pre-wrap text-left">{studentIssues}</div>
    </ArrowTooltip>
  );
}

export function TqReviewClassNotesCell({
  classNotes,
  rowId,
  compact,
}: { classNotes: string | null; rowId: string } & CompactProps) {
  if (!classNotes) return '-';
  return (
    <div className="flex items-center gap-2 text-left">
      <ArrowTooltip
        position="bottom"
        trigger={
          <div className={cn('whitespace-pre-wrap line-clamp-2', compact ? 'max-w-56' : 'max-w-72')}>{classNotes}</div>
        }
      >
        <div className="max-w-72 whitespace-pre-wrap text-left">{classNotes}</div>
      </ArrowTooltip>
      {!compact && (
        <CopyButton
          content={classNotes}
          buttonProps={{
            children: 'Copy notes',
            variant: 'outline',
            className: cn('shrink-0', classNotesActionClassName),
            iconClassName: '!h-3.5 !w-3.5',
            'data-testid': `teachers-tq-review-table-button-copy-class-notes-${rowId}`,
          }}
        />
      )}
    </div>
  );
}

export function TqReviewRecordingCell({ recordingUrls, compact }: { recordingUrls: string[] } & CompactProps) {
  if (recordingUrls.length === 0) return compact ? '-' : '—';
  return (
    <div className={cn('flex flex-col gap-1', compact ? 'max-w-36' : 'max-w-48')}>
      {recordingUrls.map((url, index) => (
        <LinkToExternal
          key={`${url}-${index}`}
          href={url}
          className="text-blue-600 hover:text-blue-800 truncate"
          title={url}
        >
          {url}
        </LinkToExternal>
      ))}
    </div>
  );
}

export function TqReviewLessonCell({
  lessonName,
  lessonId,
  compact,
}: { lessonName: string | null; lessonId: string | null } & CompactProps) {
  if (!lessonName) return compact ? '-' : '—';
  if (!lessonId) return lessonName;
  return (
    <Link to="/lessons/$lessonId" params={{ lessonId }} className="text-blue-600 hover:text-blue-800 whitespace-nowrap">
      {lessonName}
    </Link>
  );
}
